import React, { useState, useRef, useEffect } from "react";
import {
  Grid,
  TextField,
  Typography,
  Button,
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Paper,
} from "@mui/material";

import { AddCircle, RemoveCircle } from "@mui/icons-material";
import Autocomplete from "@mui/material/Autocomplete";
import apiService from "../../../ApiService";
import { API_ENDPOINTS } from "../../../ApiService";
import { useNavigate } from "react-router-dom";

const FormulaireCreationRecette = () => {
  const navigate = useNavigate();
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [currentStep, setCurrentStep] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // Adjust based on screen size
  const formRef = useRef(null); // Create a ref for the form container
  const [categories, setCategories] = useState([]);
  const [cultures, setCultures] = useState([]);
  const [dietaryPreferences, setDietaryPreferences] = useState([]);
  const [newCategory, setNewCategory] = useState("");
  const [newCulture, setNewCulture] = useState("");
  const [newDietaryPreference, setNewDietaryPreference] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [ingredientSuggestions, setIngredientSuggestions] = useState([]);
  const debounceTimer = useRef(null);

  const [recipe, setRecipe] = useState({
    nomRecette: "",
    description: "",
    nbPersonnes: 1,
    tempsPreparation: 0,
    tempsCuisson: 0,
    difficultee: 1,
    proteines: 0,
    lipides: 0,
    glucides: 0,
    calories: 0,
    ingredients: [{ ingredientName: "", quantite: "", mesure: "" }],
    steps: [{ instructions: "" }],
    culture: "",
    categories: [],
    dietaryPreferences: [],
    selectedFile: null,
    typeRecette: "",
  });

  useEffect(() => {
    // Fetch categories and cultures from backend
    const fetchCategories = async () => {
      try {
        const categoriesResponse = await apiService.get(
          API_ENDPOINTS.RECETTE_GET_CATEGORIES
        );
        console.log("categories response", categoriesResponse.data);
        setCategories(categoriesResponse.data);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    const fetchCulture = async () => {
      try {
        const culturesResponse = await apiService.get(
          API_ENDPOINTS.RECETTE_GET_CULTURE
        );
        setCultures(culturesResponse.data);
      } catch (error) {
        console.error("Error fetching cultures:", error);
      }
    };
    const fetchDietaryPreferences = async () => {
      try {
        const dietaryPreferencesResponse = await apiService.get(
          API_ENDPOINTS.RECETTE_GET_DIETARY_PREFERENCES
        );
        setDietaryPreferences(dietaryPreferencesResponse.data);
      } catch (error) {
        console.error("Error fetching dietaryPreferences:", error);
      }
    };
    fetchCategories();
    fetchCulture();
    fetchDietaryPreferences();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRecipe({ ...recipe, [name]: value });
  };

  const handleCategoryChange = (e) => {
    setRecipe({ ...recipe, categories: e.target.value });
  };

  const handleCultureChange = (e) => {
    setRecipe({ ...recipe, culture: e.target.value });
  };

  const handleDietaryPreferencesChange = (e) => {
    setRecipe({ ...recipe, dietaryPreferences: e.target.value });
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const addNewCategory = async () => {
    if (newCategory.trim() === "") return;

    try {
      const response = await apiService.post(
        API_ENDPOINTS.RECETTE_ADD_CATEGORIES_OUTSIDE_RECIPE,
        {
          recetteCategorieName: newCategory,
        }
      );
      setCategories((prev) => [...prev, response.data]);
      setRecipe((prev) => ({
        ...prev,
        categories: [...prev.categories, response.data.recetteCategorieName],
      }));
      setNewCategory("");
    } catch (error) {
      console.error("Error adding new category:", error);
    }
  };

  const addNewCulture = async () => {
    if (newCulture.trim() === "") return;

    try {
      const response = await apiService.post(
        API_ENDPOINTS.RECETTE_ADD_CULTURE_OUTSIDE_RECIPE,
        {
          cultureName: newCulture,
        }
      );
      setCultures((prev) => [...prev, response.data]);
      setRecipe((prev) => ({ ...prev, culture: response.data.cultureName }));
      setNewCulture("");
    } catch (error) {
      console.error("Error adding new culture:", error);
    }
  };

  const addNewDietaryPreference = async () => {
    if (newDietaryPreference.trim() === "") return;

    try {
      const response = await apiService.post(
        API_ENDPOINTS.ADD_DIETARY_PREFERENCE_OUTSIDE_RECIPE,
        {
          dietaryPreferenceName: newDietaryPreference,
        }
      );
      setDietaryPreferences((prev) => [...prev, response.data]);
      setRecipe((prev) => ({
        ...prev,
        dietaryPreferences: [
          ...prev.dietaryPreferences,
          response.data.dietaryPreferenceName,
        ],
      }));
      setNewDietaryPreference("");
    } catch (error) {
      console.error("Error adding new dietary preference:", error);
    }
  };

  const handleIngredientChange = (index, field, value) => {
    const newIngredients = [...recipe.ingredients];
    newIngredients[index][field] = value;
    setRecipe({ ...recipe, ingredients: newIngredients });
  };

  const handleStepChange = (index, value) => {
    const newSteps = [...recipe.steps];
    newSteps[index].instructions = value;
    setRecipe({ ...recipe, steps: newSteps });
  };

  const addIngredientRow = () => {
    setRecipe({
      ...recipe,
      ingredients: [
        ...recipe.ingredients,
        { ingredientName: "", quantite: "", mesure: "" },
      ],
    });
  };

  const removeIngredientRow = (index) => {
    const updatedIngredients = recipe.ingredients.filter((_, i) => i !== index);
    setRecipe({ ...recipe, ingredients: updatedIngredients });
  };

  const addStepRow = () => {
    setRecipe({
      ...recipe,
      steps: [...recipe.steps, { instructions: "" }],
    });
  };

  const removeStepRow = (index) => {
    const updatedSteps = recipe.steps.filter((_, i) => i !== index);
    setRecipe({ ...recipe, steps: updatedSteps });
  };

  const handleIngredientSearch = async (query) => {
    if (!query || query.length < 2) {
      setIngredientSuggestions([]);
      return;
    }
    try {
      // Replace with your actual search endpoint.
      const response = await apiService.get(
        `${API_ENDPOINTS.RECETTE_INGREDIENT_SEARCH}?query=${query}`
      );
      setIngredientSuggestions(response.data);
    } catch (error) {
      console.error("Error fetching ingredient suggestions:", error);
    }
  };

  const handleIngredientInputChange = (newInputValue) => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      handleIngredientSearch(newInputValue);
    }, 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Recette Submitted: ", recipe);

    // 1. Save basic recipe information
    const recipeData = {
      nomRecette: recipe.nomRecette,
      description: recipe.description,
      nbPersonnes: recipe.nbPersonnes,
      tempsPreparation: recipe.tempsPreparation,
      tempsCuisson: recipe.tempsCuisson,
      difficultee: recipe.difficultee,
      proteines: recipe.proteines,
      lipides: recipe.lipides,
      glucides: recipe.glucides,
      calories: recipe.calories,
      ingredients: recipe.ingredients,
      steps: recipe.steps,
      culture: recipe.culture,
      categories: recipe.categories,
      dietaryPreferences: recipe.dietaryPreferences,
      typeRecette: recipe.typeRecette,
    };

    // 2. Create FormData for multipart/form-data request
    const formData = new FormData();

    // 3. Append the JSON data as a Blob
    formData.append(
      "data",
      new Blob([JSON.stringify(recipeData)], { type: "application/json" })
    );

    // 4. Append the image file if it exists
    if (selectedFile) {
      formData.append("picture", selectedFile);
    }

    try {
      const response = await apiService.post(
        API_ENDPOINTS.CREATION_FULL_RECETTE,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      // Success message and navigation
      alert("Recipe created successfully!");
      navigate(`/home`);
    } catch (error) {
      console.error("Error creating recipe:", error);
      alert("An error occurred while creating the recipe.");
    }
  };

  return (
    <Box
      ref={formRef} // Attach the ref to the Paper
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        padding: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: "100%",
          maxWidth: "1200px", // Max width for larger screens
          minWidth: isMobile ? "300px" : "600px", // Adjust minWidth dynamically
          padding: "2rem",
          margin: "auto",
          overflow: "hidden",
        }}
      >
        {/* Step Content */}
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Nom de la recette"
                name="nomRecette"
                value={recipe.nomRecette}
                onChange={handleInputChange}
                fullWidth
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Description"
                name="description"
                value={recipe.description}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={3}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Button variant="contained" component="label">
                Upload Recipe Picture
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
              {selectedFile && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {selectedFile.name}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="culture-label">Culture</InputLabel>
                <Select
                  labelId="culture-label"
                  value={recipe.culture}
                  onChange={handleCultureChange}
                  fullWidth
                >
                  {cultures.map((culture) => (
                    <MenuItem
                      key={culture.cultureId}
                      value={culture.cultureName}
                    >
                      {culture.cultureName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box mt={2} display="flex" alignItems="center">
                <TextField
                  label="Ajouter une nouvelle culture"
                  value={newCulture}
                  onChange={(e) => setNewCulture(e.target.value)}
                  fullWidth
                />
                <Button
                  onClick={addNewCulture}
                  variant="contained"
                  color="primary"
                  sx={{ ml: 2 }}
                >
                  Ajouter
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="categories-label">Catégories</InputLabel>
                <Select
                  labelId="categories-label"
                  multiple
                  value={recipe.categories}
                  onChange={handleCategoryChange}
                  fullWidth
                >
                  {categories.map((category) => (
                    <MenuItem
                      key={category.recetteCategorieId}
                      value={category.recetteCategorieName}
                    >
                      {category.recetteCategorieName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box mt={2} display="flex" alignItems="center">
                <TextField
                  label="Ajouter une nouvelle catégorie"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  fullWidth
                />
                <Button
                  onClick={addNewCategory}
                  variant="contained"
                  color="primary"
                  sx={{ ml: 2 }}
                >
                  Ajouter
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="dietary-preferences-label">
                  Préférences alimentaires
                </InputLabel>
                <Select
                  labelId="dietary-preferences-label"
                  multiple
                  value={recipe.dietaryPreferences}
                  onChange={handleDietaryPreferencesChange}
                  fullWidth
                >
                  {dietaryPreferences.map((preference) => (
                    <MenuItem
                      key={preference.dietaryPreferencesId}
                      value={preference.dietaryPreferenceName}
                    >
                      {preference.dietaryPreferenceName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box mt={2} display="flex" alignItems="center">
                <TextField
                  label="Ajouter une nouvelle préférence alimentaire"
                  value={newDietaryPreference}
                  onChange={(e) => setNewDietaryPreference(e.target.value)}
                  fullWidth
                />
                <Button
                  onClick={addNewDietaryPreference}
                  variant="contained"
                  color="primary"
                  sx={{ ml: 2 }}
                >
                  Ajouter
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="typeRecette-label">Type de Recette</InputLabel>
                <Select
                  labelId="typeRecette-label"
                  name="typeRecette"
                  value={recipe.typeRecette}
                  onChange={handleInputChange}
                  required
                >
                  <MenuItem value="Sucrée">Sucrée</MenuItem>
                  <MenuItem value="Salée">Salée</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Nombre de personnes"
                name="nbPersonnes"
                type="number"
                value={recipe.nbPersonnes}
                onChange={handleInputChange}
                fullWidth
                required
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Temps de préparation (minutes)"
                name="tempsPreparation"
                type="number"
                value={recipe.tempsPreparation}
                onChange={handleInputChange}
                fullWidth
                required
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Temps de cuisson (minutes)"
                name="tempsCuisson"
                type="number"
                value={recipe.tempsCuisson}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Difficulté (1-5)"
                name="difficultee"
                type="number"
                value={recipe.difficultee}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={3}>
              <TextField
                label="Protéines (g)"
                name="proteines"
                type="number"
                value={recipe.proteines}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={3}>
              <TextField
                label="Lipides (g)"
                name="lipides"
                type="number"
                value={recipe.lipides}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={3}>
              <TextField
                label="Glucides (g)"
                name="glucides"
                type="number"
                value={recipe.glucides}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={3}>
              <TextField
                label="Calories"
                name="calories"
                type="number"
                value={recipe.calories}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Ingredients
              </Typography>
              {recipe.ingredients.map((ingredient, index) => (
                <Grid container spacing={2} key={index} alignItems="center">
                  <Grid item xs={4}>
                    <Autocomplete
                      freeSolo
                      options={ingredientSuggestions.map(
                        (option) => option.ingredientName
                      )}
                      // When the input text changes, update the suggestions and the ingredient value.
                      onInputChange={(event, newInputValue, reason) => {
                        if (reason === "input") {
                          handleIngredientInputChange(newInputValue);
                          handleIngredientChange(
                            index,
                            "ingredientName",
                            newInputValue
                          );
                        }
                      }}
                      // When a suggestion is selected, update the ingredient name.
                      onChange={(event, newValue) => {
                        // newValue can be a string (if freeSolo) or an object.
                        const value =
                          typeof newValue === "string"
                            ? newValue
                            : newValue?.ingredientName || "";
                        handleIngredientChange(index, "ingredientName", value);
                      }}
                      value={ingredient.ingredientName}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Ingredient Name"
                          variant="outlined"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      label="Quantity"
                      value={ingredient.quantite}
                      onChange={(e) =>
                        handleIngredientChange(
                          index,
                          "quantite",
                          e.target.value
                        )
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      label="Measurement"
                      value={ingredient.mesure}
                      onChange={(e) =>
                        handleIngredientChange(index, "mesure", e.target.value)
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <IconButton
                      color="error"
                      onClick={() => removeIngredientRow(index)}
                    >
                      <RemoveCircle />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
              <Button
                variant="contained"
                color="primary"
                onClick={addIngredientRow}
                startIcon={<AddCircle />}
              >
                Add Ingredient
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Steps
              </Typography>
              {recipe.steps.map((step, index) => (
                <Grid container spacing={2} key={index} alignItems="center">
                  <Grid item xs={10}>
                    <TextField
                      label={`Step ${index + 1}`}
                      value={step.instructions}
                      onChange={(e) => handleStepChange(index, e.target.value)}
                      fullWidth
                      multiline
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <IconButton
                      color="error"
                      onClick={() => removeStepRow(index)}
                    >
                      <RemoveCircle />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
              <Button
                variant="contained"
                color="primary"
                onClick={addStepRow}
                startIcon={<AddCircle />}
              >
                Add Step
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="success"
                fullWidth
              >
                Create Recipe
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default FormulaireCreationRecette;
